import {useEffect, useMemo, useState} from 'react';
import styles from './IssueSIZFromNeed.module.css';
import Button from '../../Button/Button';
import InputForm from '../../InputForm/InputForm';
import {SelectForm} from '../../SelectForm/SelectForm';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import Spinner from '../../Spinner/Spinner';
import useApi from '../../../hooks/useApi.hook';
import {notifyDownloadQueued} from '../../../helpers/downloadsEvents';
import {
	createMaterialsBulkUsersUrl,
	issueSizGenerateZipUrl
} from '../../../helpers/constants';

const todayIso = () => {
	const date = new Date();
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export function formatSizDocNumber(personnelNumber, dateStr) {
	if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
		return personnelNumber || '';
	}
	const [, year, month, day] = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	const ddmmyy = `${day}${month}${year.slice(2)}`;
	return personnelNumber ? `${personnelNumber}-${ddmmyy}` : ddmmyy;
}

function defaultQuantity(need) {
	const value = Number(need);
	if (Number.isFinite(value) && value > 0) {
		return Math.max(1, Math.floor(value));
	}
	return 1;
}

function toIssueRow(record, startDate) {
	return {
		id: String(record.id),
		fio: record.last_name_with_initials || record.user || '—',
		given: record.given ?? 0,
		norm: record.norm ?? 0,
		number: record.number || '',
		number_of_document: formatSizDocNumber(record.number, startDate),
		docNumberManual: false,
		quantity: defaultQuantity(record.need),
		period: Number(record.period) > 0 ? Number(record.period) : 365,
		sertificate: '',
		brand: null
	};
}

function buildPayload(rows, materialTypeId, startDate) {
	return {
		items: rows.map((row) => ({
			user_id: row.id,
			number_of_document: row.number_of_document || null,
			materials_data: [{
				material_type_id: Number(materialTypeId),
				sertificate: row.sertificate || null,
				start_date: startDate || null,
				period: row.period,
				quantity: row.quantity,
				brand: row.brand || null
			}]
		}))
	};
}

function formatDetail(detail, fallback) {
	if (detail == null) {
		return fallback;
	}
	if (typeof detail === 'string') {
		return detail;
	}
	if (Array.isArray(detail)) {
		return detail.map((item) => item.msg || item.detail || String(item)).join('; ') || fallback;
	}
	return fallback;
}

async function errorFromResponse(error, fallback) {
	const data = error?.response?.data;
	if (!data) {
		return error?.message || fallback;
	}
	if (typeof data === 'string') {
		return data;
	}
	if (data instanceof Blob) {
		try {
			const text = await data.text();
			const parsed = JSON.parse(text);
			return formatDetail(parsed.detail, fallback);
		} catch {
			return fallback;
		}
	}
	return formatDetail(data.detail, data.message || fallback);
}

export function IssueSIZFromNeed({
	selectedRecords,
	materialTypeId,
	materialTypeTitle,
	typesDict,
	onSuccess,
	onBusyChange,
	onCreated
}) {
	const api = useApi();
	const [startDate, setStartDate] = useState(todayIso);
	const [rows, setRows] = useState([]);
	const [error, setError] = useState(undefined);
	const [submitting, setSubmitting] = useState(false);
	const [created, setCreated] = useState(false);
	// ids of the rows /materials/bulk_users actually wrote: the zip endpoint
	// prints from those, so a failed download is retried without re-issuing.
	const [createdMaterialIds, setCreatedMaterialIds] = useState(null);

	useEffect(() => {
		const date = todayIso();
		setStartDate(date);
		setRows((selectedRecords || []).map((record) => toIssueRow(record, date)));
		setError(undefined);
		setCreated(false);
		setCreatedMaterialIds(null);
	}, [selectedRecords]);

	const brandOptions = useMemo(() => {
		const brands = typesDict?.[String(materialTypeId)]?.brands || [];
		return brands.map((brand) => ({value: brand, label: brand}));
	}, [typesDict, materialTypeId]);

	const updateRow = (id, patch) => {
		setRows((prev) => prev.map((row) => (row.id === id ? {...row, ...patch} : row)));
	};

	const setStartDateAndNumbers = (event) => {
		const raw = event?.target?.value ?? '';
		const nextDate = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : todayIso();
		setStartDate(nextDate);
		setRows((prev) => prev.map((row) => {
			if (row.docNumberManual) {
				return row;
			}
			return {
				...row,
				number_of_document: formatSizDocNumber(row.number, nextDate)
			};
		}));
	};

	const handleNumber = (id, event) => {
		const name = event.target.name;
		const raw = event.target.valueAsNumber ?? Number(event.target.value);
		const value = Number.isFinite(raw) ? Math.max(1, Math.floor(raw)) : 1;
		updateRow(id, {[name]: value});
	};

	const submitDisabled = !rows.length
		|| !startDate
		|| submitting
		|| rows.some((row) => !row.quantity || row.quantity < 1 || !row.period || row.period < 1);
	const fieldsLocked = submitting || created;

	const handleSubmit = async () => {
		if (submitDisabled) {
			return;
		}
		setError(undefined);
		setSubmitting(true);
		onBusyChange?.(true);
		let materialIds = createdMaterialIds;
		try {
			if (!materialIds) {
				const response = await api.post(
					createMaterialsBulkUsersUrl,
					buildPayload(rows, materialTypeId, startDate)
				);
				materialIds = (response.data || []).map((material) => material.id);
				setCreated(true);
				setCreatedMaterialIds(materialIds);
				onCreated?.();
			}
		} catch (e) {
			setError(await errorFromResponse(e, 'Не удалось выдать СИЗ'));
			setSubmitting(false);
			onBusyChange?.(false);
			return;
		}
		try {
			const response = await api.post(issueSizGenerateZipUrl, {material_ids: materialIds});
			notifyDownloadQueued(response.data);
			onBusyChange?.(false);
			onSuccess();
		} catch (e) {
			setError(await errorFromResponse(
				e,
				'СИЗ выданы, но не удалось сформировать документы. Можно закрыть окно и распечатать позже.'
			));
			setSubmitting(false);
			onBusyChange?.(false);
		}
	};

	return (
		<div className={styles.wrap}>
			<div className={styles.header}>
				<h1 className={styles.title}>{materialTypeTitle || 'Выдать СИЗ'}</h1>
				<label className={styles.dateLabel}>
					Дата документа
					<InputForm
						value={startDate}
						type="date"
						name="start_date"
						onChange={setStartDateAndNumbers}
						disabled={fieldsLocked}
					/>
				</label>
			</div>
			<ErrorMessage error={error} onClose={() => setError(undefined)} />
			{submitting && (
				<div className={styles.loading}>
					<Spinner />
				</div>
			)}
			<div className={styles.tableWrap}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>ФИО</th>
							<th title="выдано / норма"></th>
							<th>Номер документа</th>
							<th>Кол-во</th>
							<th>Период дней</th>
							<th>Сертификат</th>
							<th>Марка СИЗ</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const selectedBrand = brandOptions.find((opt) => opt.value === row.brand) || null;
							return (
								<tr key={row.id}>
									<td className={styles.fio}>{row.fio}</td>
									<td className={styles.need}>{row.given}/{row.norm}</td>
									<td>
										<InputForm
											value={row.number_of_document}
											type="text"
											name="number_of_document"
											disabled={fieldsLocked}
											onChange={(event) => updateRow(row.id, {
												number_of_document: event.target.value,
												docNumberManual: true
											})}
										/>
									</td>
									<td className={styles.narrow}>
										<InputForm
											value={row.quantity}
											type="number"
											name="quantity"
											min={1}
											step={1}
											disabled={fieldsLocked}
											onChange={(event) => handleNumber(row.id, event)}
										/>
									</td>
									<td className={styles.narrow}>
										<InputForm
											value={row.period}
											type="number"
											name="period"
											min={1}
											step={1}
											disabled={fieldsLocked}
											onChange={(event) => handleNumber(row.id, event)}
										/>
									</td>
									<td>
										<InputForm
											value={row.sertificate}
											type="text"
											name="sertificate"
											placeholder="Сертификат"
											disabled={fieldsLocked}
											onChange={(event) => updateRow(row.id, {
												sertificate: event.target.value
											})}
										/>
									</td>
									<td className={styles.brand}>
										<SelectForm
											value={selectedBrand}
											options={brandOptions}
											placeholder="—"
											isClearable
											isDisabled={fieldsLocked || !brandOptions.length}
											menuPortalTarget={document.body}
											onChange={(opt) => updateRow(row.id, {
												brand: opt ? opt.value : null
											})}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<div className={styles.submit}>
				<Button type="button" onClick={handleSubmit} disabled={submitDisabled}>
					{created ? 'Сформировать документы' : 'Выдать и распечатать'}
				</Button>
			</div>
		</div>
	);
}
